/**
 * Runtime host-mode detection for the marketing-vs-app subdomain split.
 *
 *   teesync.co.uk           → 'marketing'   (Landing always shown on /)
 *   app.teesync.co.uk       → 'app'         (auth-conditional /; no marketing routes)
 *   localhost / previews    → 'unified'     (both available, mirrors dev)
 *
 * iOS Capacitor shells run inside their own scheme (capacitor://localhost)
 * and are always treated as 'app' - they ship the SPA and never visit the
 * apex domain at runtime.
 */

import { isNative } from '../services/platformService'

const APP_HOST = 'app.teesync.co.uk'
const MARKETING_HOSTS = new Set(['teesync.co.uk', 'www.teesync.co.uk'])

const APP_ORIGIN = 'https://app.teesync.co.uk'
const MARKETING_ORIGIN = 'https://teesync.co.uk'

const isBrowser = typeof window !== 'undefined'

/**
 * @returns 'marketing' | 'app' | 'unified'
 */
export function getHostMode() {
  if (!isBrowser) return 'unified'
  // Capacitor native shells (iOS, Android) are always the app.
  if (isNative()) return 'app'
  const host = window.location.hostname
  if (MARKETING_HOSTS.has(host)) return 'marketing'
  if (host === APP_HOST) return 'app'
  return 'unified'
}

export function isMarketingHost() {
  return getHostMode() === 'marketing'
}

export function isAppHost() {
  return getHostMode() === 'app'
}

/**
 * Resolve a path to a fully-qualified URL on the app subdomain.
 *   appUrl('/signup') → 'https://app.teesync.co.uk/signup'    (marketing host)
 *   appUrl('/signup') → '/signup'                              (app or unified)
 *
 * Use for marketing-page CTAs that should jump to the app subdomain in
 * production but stay relative in dev / previews.
 */
export function appUrl(path = '/') {
  return getHostMode() === 'marketing' ? `${APP_ORIGIN}${path}` : path
}

/**
 * Mirror of appUrl() for the marketing subdomain - used by the app to point
 * back to brochure content when needed.
 */
export function marketingUrl(path = '/') {
  return getHostMode() === 'app' ? `${MARKETING_ORIGIN}${path}` : path
}

/**
 * Origin for public, shareable links (guest round links at /r/:code). These
 * must resolve to the apex marketing domain per environment - never the app
 * subdomain (recipients aren't logged in) nor the Capacitor scheme:
 *   production → https://teesync.co.uk
 *   staging    → https://staging.teesync.co.uk
 *   local dev  → http://localhost:xxxx (whatever the dev server is on)
 *
 * Environment is taken from Vite's build mode (`import.meta.env.MODE`), which
 * already maps to environment everywhere else: `npm run dev` and the staging
 * deploys build with `--mode staging`; the production build is mode
 * 'production'. Local browser dev is intercepted first so links are testable
 * without a deploy.
 */
export function publicSiteOrigin() {
  if (isBrowser && !isNative()) {
    const host = window.location.hostname
    if (host === 'localhost' || host === '127.0.0.1' || /^\d+\.\d+\.\d+\.\d+$/.test(host)) {
      return window.location.origin
    }
  }
  return import.meta.env.MODE === 'staging'
    ? 'https://staging.teesync.co.uk'
    : MARKETING_ORIGIN
}

/**
 * Fully-qualified public URL for a guest round link.
 *   guestRoundUrl('A1B2C3D4') → 'https://teesync.co.uk/r/A1B2C3D4' (prod)
 */
export function guestRoundUrl(shortCode) {
  return `${publicSiteOrigin()}/r/${shortCode}`
}

/**
 * Fully-qualified public URL for a group invite link.
 *   groupInviteUrl('ABCD1234') → 'https://teesync.co.uk/join/ABCD1234' (prod)
 *
 * Deliberately the apex, not app.teesync.co.uk. App.entitlements claims
 * `applinks:teesync.co.uk` only and the AASA already claims /join/*, so a tap
 * with the app installed opens the app directly and never issues the HTTP
 * request. Pointing this at the app subdomain would need an entitlement change
 * and would land app-less recipients on the authed shell.
 */
export function groupInviteUrl(inviteCode) {
  return `${publicSiteOrigin()}/join/${inviteCode}`
}

/**
 * True on Android browsers. There is no Android app, so the invite landing
 * page must offer the web flow rather than an App Store badge. False inside
 * Capacitor, which is the app already.
 */
export function isAndroid() {
  if (!isBrowser) return false
  if (isNative()) return false
  return /Android/.test(window.navigator.userAgent)
}

/**
 * True for Safari on a real iPhone/iPad - used by marketing pages to swap
 * the primary CTA to the App Store and let Apple's Smart App Banner do the
 * rest. False inside Capacitor (already the native app), Chrome/Firefox on
 * iOS, in-app webviews, and anything non-iOS.
 *
 * iPad-on-iOS-13+ reports as macOS in the UA string; we sniff for touch
 * support as a secondary signal so iPad Safari is still detected.
 */
export function isIOSSafari() {
  if (!isBrowser) return false
  if (isNative()) return false
  const ua = window.navigator.userAgent
  const isIPhone = /iPhone|iPod/.test(ua)
  const isIPadUA = /iPad/.test(ua)
  const isMacWithTouch =
    /Macintosh/.test(ua) && typeof navigator.maxTouchPoints === 'number' && navigator.maxTouchPoints > 1
  const isIOS = isIPhone || isIPadUA || isMacWithTouch
  if (!isIOS) return false
  // Exclude Chrome (CriOS), Firefox (FxiOS), Edge (EdgiOS), and webviews
  // (which omit "Safari" from the UA).
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua)
  return isSafari
}
